import './HistoryStep.css';
import { useState } from 'react';
import { Search, Clock, Zap, RotateCcw, ArrowLeft } from 'lucide-react';
import useVoiceDumpStore from '../../stores/useVoiceDumpStore';
import { BUILTIN_TEMPLATES } from '../../utils/geminiService';

function timeAgo(dateStr) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;

  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getTemplateEmoji(templateId, customTemplates) {
  const builtin = BUILTIN_TEMPLATES.find(t => t.id === templateId);
  if (builtin) return builtin.emoji;
  const custom = customTemplates.find(t => t.id === templateId);
  return custom?.emoji || '🧠';
}

export default function HistoryStep() {
  const dumpHistory = useVoiceDumpStore(s => s.dumpHistory);
  const customTemplates = useVoiceDumpStore(s => s.customTemplates);
  const replayDump = useVoiceDumpStore(s => s.replayDump);
  const [search, setSearch] = useState('');

  // Navigate back to recording
  const setStep = () => useVoiceDumpStore.setState({ step: 'recording' });

  const filteredHistory = search.trim()
    ? dumpHistory.filter(entry =>
        entry.transcript?.toLowerCase().includes(search.toLowerCase()) ||
        entry.summary?.toLowerCase().includes(search.toLowerCase())
      )
    : dumpHistory;

  return (
    <div className="vd-history">
      <div className="vd-history__header">
        <button className="btn btn-ghost btn-sm btn-icon vd-history__back" onClick={setStep} type="button" aria-label="Back to recording">
          <ArrowLeft size={18} />
        </button>
        <h3 className="vd-history__title">
          <Clock size={16} /> Dump History
        </h3>
      </div>

      {/* Search */}
      <div className="vd-history__search">
        <Search size={14} />
        <input
          type="text"
          placeholder="Search past dumps..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="vd-history__search-input"
        />
      </div>

      {/* List */}
      {filteredHistory.length === 0 ? (
        <div className="vd-empty">
          <div className="vd-empty__icon">{search ? '🔍' : '📭'}</div>
          <p className="vd-empty__text">
            {search
              ? `No dumps matching "${search}"`
              : 'No past dumps yet. Your history will appear here after you complete a voice dump.'}
          </p>
        </div>
      ) : (
        <div className="vd-history__list">
          {filteredHistory.map((entry, i) => (
            <button
              key={entry.id}
              className="vd-history__item"
              onClick={() => replayDump(entry.id)}
              type="button"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <div className="vd-history__item-top">
                <span className="vd-history__item-emoji">
                  {getTemplateEmoji(entry.templateId, customTemplates)}
                </span>
                <span className="vd-history__item-time">{timeAgo(entry.timestamp)}</span>
              </div>

              <p className="vd-history__item-transcript">
                {entry.transcript
                  ? entry.transcript.length > 100
                    ? entry.transcript.slice(0, 100) + '…'
                    : entry.transcript
                  : 'No transcript'}
              </p>

              <div className="vd-history__item-meta">
                <span className="vd-history__item-badge">
                  <Zap size={10} />
                  {entry.actionCount || entry.actions?.length || 0} actions
                </span>
                {entry.xpEarned > 0 && (
                  <span className="vd-history__item-xp">+{entry.xpEarned} XP</span>
                )}
                <span className="vd-history__item-replay">
                  <RotateCcw size={10} /> Re-apply
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
